import type { ConsentState } from './types';
import {
  CONSENT_COOKIE_NAME,
  CONSENT_COOKIE_MAX_AGE_DAYS,
  CONSENT_COOKIE_PATH,
  CONSENT_COOKIE_SAME_SITE,
  CONSENT_COOKIE_DOMAIN_SUFFIX,
  CONSENT_COOKIE_DOMAIN,
} from './constants';

/**
 * Internal-only storage adapter — the ONLY place in the codebase that reads or
 * writes `document.cookie` for consent. Nothing outside `lib/consent` should
 * touch the consent cookie directly; go through the semantic functions in
 * `consent.ts` instead. (Same discipline as `dataLayer.ts` in the analytics
 * foundation: one writer, so the wire format can never drift per call site.)
 *
 * Cookies are used rather than localStorage so the answer is shared across
 * subdomains (agapae.gr ↔ app.agapae.gr ↔ invite.agapae.gr) via
 * `domain=.agapae.gr`.
 *
 * The exact attribute string produced here is a byte-for-byte carry-over of the
 * banner's original helper. It is deliberately unchanged: records written before
 * this module existed must keep round-tripping, and Phase 7.0A is a pure
 * centralization with no storage change. Attribute changes (e.g. adding
 * `Secure`) belong to a later phase — see docs/analytics/consent-foundation.md.
 */

/**
 * The `; domain=` attribute, or an empty string when the current host is not a
 * production Agapaé host (localhost runs the three apps on ports, not
 * subdomains, so a domain attribute there would make the browser drop the
 * cookie entirely).
 *
 * NOTE: the substring test is the original banner's check, carried over
 * verbatim. It is looser than the host-equality test used by
 * `app/src/utils/sessionFlagCookie.ts`; reconciling the two is a tracked
 * follow-up, not a Phase 7.0A change, because tightening it here would alter
 * cookie scope on some hosts.
 */
function domainAttribute(): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  if (hostname.includes(CONSENT_COOKIE_DOMAIN_SUFFIX)) {
    return `; domain=${CONSENT_COOKIE_DOMAIN}`;
  }
  return '';
}

/**
 * Read and parse the stored consent record.
 *
 * Returns `null` for every "no usable record" case — cookie absent, malformed,
 * or not valid JSON — so a corrupted cookie re-prompts the visitor rather than
 * throwing or silently granting anything.
 */
export function readConsentCookie(): ConsentState | null {
  if (typeof document === 'undefined') return null;

  const nameEQ = CONSENT_COOKIE_NAME + '=';
  for (let cookie of document.cookie.split(';')) {
    cookie = cookie.trim();
    if (cookie.startsWith(nameEQ)) {
      try {
        const parsed: unknown = JSON.parse(decodeURIComponent(cookie.substring(nameEQ.length)));
        // The cookie is untrusted input, and a record written by an older build
        // may be partial. TypeScript's view of the parsed shape is therefore
        // optimistic by design: `getConsent()` normalizes field by field, while
        // `getStoredConsent()` deliberately hands back whatever was found.
        return parsed as ConsentState | null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

/** Persist a consent record, scoped to every Agapaé surface. */
export function writeConsentCookie(state: ConsentState): void {
  if (typeof document === 'undefined') return;

  const date = new Date();
  date.setTime(date.getTime() + CONSENT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
  const expires = '; expires=' + date.toUTCString();

  document.cookie =
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))}${expires}` +
    `; path=${CONSENT_COOKIE_PATH}; SameSite=${CONSENT_COOKIE_SAME_SITE}` +
    domainAttribute();
}
